import React from 'react';
import { useTableContext } from '../../context';
import { BulkActionsToolbar } from '../toolbar/bulk-actions-toolbar';
import { BulkAction } from '../../types';

/**
 * Props for TableBulkActionsSection component
 */
export interface TableBulkActionsSectionProps {
  /** Array of bulk action configurations */
  bulkActions?: BulkAction<any>[];
}

/**
 * TableBulkActionsSection component - Renders the bulk actions toolbar
 *
 * This component renders the bulk actions toolbar when items are selected
 * and bulk actions are configured. It uses the table context for selection state.
 *
 * @template T - The type of data rows in the table
 */
export function TableBulkActionsSection<T extends Record<string, unknown>>({
  bulkActions = [],
}: TableBulkActionsSectionProps) {
  const { showBulkActions, selection, actualRows, getRowId } =
    useTableContext<T>();

  if (!showBulkActions || bulkActions.length === 0) {
    return null;
  }

  const handleClearSelection = () => {
    // Clear selected state through the selection hook
    if (selection.handleSelectAll) {
      selection.handleSelectAll([], getRowId);
    }
  };

  const selectedRowData = selection.selected
    .map((id) => actualRows.find((row) => getRowId(row) === id))
    .filter(Boolean) as T[];

  return (
    <BulkActionsToolbar
      selectedRows={selectedRowData}
      selectedIds={selection.selected}
      bulkActions={bulkActions}
      onClearSelection={handleClearSelection}
    />
  );
}

TableBulkActionsSection.displayName = 'TableBulkActionsSection';
